import React, { useEffect, useMemo } from 'react';
import { ArrowRight } from 'lucide-react';
import SEO_TOPIC_PAGES from './seoTopicContent.json';
import SeoTopicRedesign from './SeoTopicRedesign';

const SITE_URL = 'https://commetriq.de';
const DEFAULT_TITLE = 'commetriq | E-Commerce Controlling für JTL-Händler';
const DEFAULT_DESCRIPTION = 'commetriq verbindet JTL-Wawi, Kosten und Verkaufskanäle in einem Controlling-Dashboard für Deckungsbeitrag, Lager, Retouren und Werbung.';

const TOPIC_LINKS = SEO_TOPIC_PAGES.map(page => [page.path, page.linkLabel]);

export { SEO_TOPIC_PAGES };

const normalizePath = (path) => {
    const clean = String(path || '').split('?')[0].split('#')[0].replace(/\/+$/, '');
    return clean || '/';
};

export const getSeoTopicPage = (path) => SEO_TOPIC_PAGES.find(page => page.path === normalizePath(path)) || null;
export const isSeoTopicPath = (path) => Boolean(getSeoTopicPage(path));

const setMeta = (selector, attrs) => {
    let element = document.head.querySelector(selector);
    if (!element) {
        element = document.createElement(attrs.property ? 'meta' : attrs.rel ? 'link' : 'meta');
        document.head.appendChild(element);
    }
    Object.entries(attrs).forEach(([key, value]) => element.setAttribute(key, value));
};

const removeElement = (id) => {
    const element = document.getElementById(id);
    if (element) element.remove();
};

const useSeoMeta = (page) => {
    useEffect(() => {
        if (!page || typeof document === 'undefined') return undefined;

        const url = `${SITE_URL}${page.path}`;
        document.title = page.title;
        setMeta('meta[name="description"]', { name: 'description', content: page.description });
        setMeta('meta[name="robots"]', { name: 'robots', content: 'index,follow,max-image-preview:large' });
        setMeta('link[rel="canonical"]', { rel: 'canonical', href: url });
        setMeta('meta[property="og:type"]', { property: 'og:type', content: 'website' });
        setMeta('meta[property="og:url"]', { property: 'og:url', content: url });
        setMeta('meta[property="og:title"]', { property: 'og:title', content: page.title });
        setMeta('meta[property="og:description"]', { property: 'og:description', content: page.description });
        setMeta('meta[property="og:image"]', { property: 'og:image', content: `${SITE_URL}${page.socialImage || page.image}` });
        setMeta('meta[property="og:image:alt"]', { property: 'og:image:alt', content: page.socialImage ? 'commetriq E-Commerce-Controlling-Dashboard mit Umsatz, Marge, Lager und Retouren' : page.imageAlt });
        setMeta('meta[name="twitter:card"]', { name: 'twitter:card', content: 'summary_large_image' });
        setMeta('meta[name="twitter:title"]', { name: 'twitter:title', content: page.title });
        setMeta('meta[name="twitter:description"]', { name: 'twitter:description', content: page.description });
        setMeta('meta[name="twitter:image"]', { name: 'twitter:image', content: `${SITE_URL}${page.socialImage || page.image}` });
        setMeta('meta[name="twitter:image:alt"]', { name: 'twitter:image:alt', content: page.socialImage ? 'commetriq E-Commerce Reporting' : page.imageAlt });

        removeElement('seo-topic-jsonld');
        const script = document.createElement('script');
        script.id = 'seo-topic-jsonld';
        script.type = 'application/ld+json';
        script.textContent = JSON.stringify({
            '@context': 'https://schema.org',
            '@graph': [
                {
                    '@type': 'WebPage',
                    '@id': `${url}#webpage`,
                    url,
                    name: page.title,
                    description: page.description,
                    inLanguage: 'de-DE',
                    isPartOf: { '@id': `${SITE_URL}/#website` },
                },
                {
                    '@type': 'BreadcrumbList',
                    '@id': `${url}#breadcrumb`,
                    itemListElement: [
                        { '@type': 'ListItem', position: 1, name: 'Startseite', item: `${SITE_URL}/` },
                        { '@type': 'ListItem', position: 2, name: page.eyebrow, item: url },
                    ],
                },
                {
                    '@type': 'FAQPage',
                    '@id': `${url}#faq`,
                    mainEntity: page.faq.map(([question, answer]) => ({
                        '@type': 'Question',
                        name: question,
                        acceptedAnswer: { '@type': 'Answer', text: answer },
                    })),
                },
            ],
        });
        document.head.appendChild(script);

        return () => {
            removeElement('seo-topic-jsonld');
            document.title = DEFAULT_TITLE;
            setMeta('meta[name="description"]', { name: 'description', content: DEFAULT_DESCRIPTION });
            setMeta('link[rel="canonical"]', { rel: 'canonical', href: `${SITE_URL}/` });
            setMeta('meta[property="og:url"]', { property: 'og:url', content: `${SITE_URL}/` });
            setMeta('meta[property="og:title"]', { property: 'og:title', content: DEFAULT_TITLE });
            setMeta('meta[property="og:description"]', { property: 'og:description', content: DEFAULT_DESCRIPTION });
            setMeta('meta[property="og:image"]', { property: 'og:image', content: `${SITE_URL}/og-image-cometriq-dashboard-v4.png` });
            setMeta('meta[property="og:image:alt"]', { property: 'og:image:alt', content: 'commetriq E-Commerce-Controlling-Dashboard mit Umsatz, Marge, Lager und Retouren' });
            setMeta('meta[name="twitter:title"]', { name: 'twitter:title', content: DEFAULT_TITLE });
            setMeta('meta[name="twitter:description"]', { name: 'twitter:description', content: DEFAULT_DESCRIPTION });
            setMeta('meta[name="twitter:image"]', { name: 'twitter:image', content: `${SITE_URL}/og-image-cometriq-dashboard-v4.png` });
            setMeta('meta[name="twitter:image:alt"]', { name: 'twitter:image:alt', content: 'commetriq E-Commerce-Controlling-Dashboard mit Umsatz, Marge, Lager und Retouren' });
        };
    }, [page]);
};

const TopicHeader = ({ onLogin }) => (
    <header className="sticky top-0 z-50 border-b border-slate-200/70 bg-white/90 px-4 py-5 backdrop-blur-xl">
        <nav className="mx-auto flex max-w-[1600px] items-center justify-between gap-4">
            <a href="/" className="inline-flex items-center">
                <img src="/commetriq-logo-light.webp" alt="commetriq Logo" className="h-9 w-auto max-w-[180px] object-contain" />
            </a>
            <div className="hidden items-center gap-7 text-sm font-semibold text-slate-600 md:flex">
                <a href="/#solutions" className="hover:text-slate-950">Produkt</a>
                <a href="/#preise" className="hover:text-slate-950">Preise</a>
                <a href="/#faq" className="hover:text-slate-950">FAQ</a>
            </div>
            <button type="button" onClick={onLogin} className="rounded-lg border border-slate-200 bg-white px-4 py-2.5 text-sm font-semibold text-slate-950 shadow-sm transition hover:-translate-y-0.5">
                Login
            </button>
        </nav>
    </header>
);

const TopicFooter = ({ onLogin }) => (
    <footer className="border-t border-slate-200 bg-white px-4 py-10">
        <div className="mx-auto grid max-w-[1600px] gap-8 md:grid-cols-[1fr_1fr_0.8fr]">
            <div>
                <img src="/commetriq-logo-light.webp" alt="commetriq Logo" className="h-8 w-auto max-w-[170px] object-contain" />
                <p className="mt-4 max-w-md text-sm leading-7 text-slate-500">JTL-Wawi Dashboard für Umsatz, Marge, Lager, Retouren, Kunden und Reports.</p>
            </div>
            <div>
                <p className="text-sm font-semibold text-slate-950">Themen</p>
                <div className="mt-4 grid gap-3 text-sm font-medium text-slate-500 sm:grid-cols-2">
                    {TOPIC_LINKS.map(([href, label]) => <a key={href} href={href} className="hover:text-slate-950">{label}</a>)}
                </div>
            </div>
            <div>
                <p className="text-sm font-semibold text-slate-950">Produkt</p>
                <div className="mt-4 grid gap-3 text-sm font-medium text-slate-500">
                    <a href="/" className="hover:text-slate-950">Startseite</a>
                    <a href="/#preise" className="hover:text-slate-950">Preise</a>
                    <a href="/impressum" className="hover:text-slate-950">Impressum</a>
                    <a href="/datenschutz" className="hover:text-slate-950">Datenschutz</a>
                    <a href="/agb" className="hover:text-slate-950">B2B-AGB</a>
                    <a href="/avv" className="hover:text-slate-950">AVV</a>
                    <button type="button" onClick={onLogin} className="text-left hover:text-slate-950">Login</button>
                </div>
            </div>
        </div>
    </footer>
);

export default function SeoTopicPage({ path, onLogin, onDemoOpen, onLeadRequest }) {
    const page = useMemo(() => getSeoTopicPage(path), [path]);
    useSeoMeta(page);

    if (!page) return null;

    const relatedLinks = TOPIC_LINKS.filter(([href]) => href !== page.path);

    if (page.redesign) {
        return <SeoTopicRedesign page={page} relatedLinks={relatedLinks} onLogin={onLogin} onDemoOpen={onDemoOpen} onLeadRequest={onLeadRequest} />;
    }

    return (
        <div className="min-h-screen bg-white text-slate-950" style={{ fontFamily: 'Satoshi, ui-sans-serif, system-ui, -apple-system, BlinkMacSystemFont, Segoe UI, sans-serif' }}>
            <TopicHeader onLogin={onLogin} />
            <main>
                <section className="px-5 py-14 sm:px-8 lg:px-10 lg:py-20">
                    <div className="mx-auto grid max-w-[1600px] gap-10 lg:grid-cols-[0.92fr_1.08fr] lg:items-center lg:gap-16">
                        <div>
                            <p className="text-xs font-bold uppercase tracking-[0.1em] text-orange-700">{page.eyebrow}</p>
                            <h1 className="mt-5 max-w-3xl text-4xl font-semibold leading-[1.08] tracking-[-0.05em] text-slate-950 sm:text-5xl lg:text-[3.75rem]">{page.h1}</h1>
                            <p className="mt-6 max-w-xl text-base leading-7 text-slate-600 sm:text-lg sm:leading-8">{page.intro}</p>
                            <ul className="mt-7 max-w-xl divide-y divide-slate-200 border-y border-slate-200">
                                {page.highlights.map(item => (
                                    <li key={item} className="flex items-start gap-3 py-3 text-sm font-semibold leading-6 text-slate-700">
                                        <span aria-hidden="true" className="mt-2 h-1.5 w-1.5 shrink-0 rounded-full bg-orange-600" />
                                        {item}
                                    </li>
                                ))}
                            </ul>
                            <div className="mt-8 flex flex-wrap items-center gap-x-6 gap-y-3">
                                <button type="button" onClick={onLeadRequest} className="inline-flex min-h-12 items-center justify-center gap-2 rounded-lg bg-orange-600 px-5 py-3 text-sm font-semibold text-white transition hover:bg-orange-700">
                                    20-Minuten-Gespräch anfragen <ArrowRight className="h-4 w-4" />
                                </button>
                                <button type="button" onClick={onDemoOpen} className="inline-flex items-center gap-2 py-3 text-sm font-semibold text-slate-800 hover:text-orange-700">
                                    Demo ansehen <ArrowRight className="h-4 w-4 text-orange-600" />
                                </button>
                            </div>
                        </div>
                        <div className="overflow-hidden rounded-xl border border-slate-200 bg-white p-2 lg:p-3">
                            <img src={page.image} alt={page.imageAlt} className="h-auto w-full object-contain" loading="eager" decoding="async" />
                        </div>
                    </div>
                </section>

                <section className="px-5 py-12 sm:px-8 lg:px-10">
                    <div className={`mx-auto grid max-w-[1600px] gap-0 border-y border-slate-200 ${page.sections.length === 4 ? 'md:grid-cols-2 xl:grid-cols-4' : 'md:grid-cols-3'}`}>
                        {page.sections.map(([title, text]) => (
                            <article key={title} className="border-b border-slate-200 py-6 md:border-b-0 md:border-r md:px-6 md:py-7 first:md:pl-0 last:md:border-r-0 last:md:pr-0">
                                <h2 className="text-xl font-semibold leading-snug tracking-tight text-slate-950">{title}</h2>
                                <p className="mt-3 text-sm leading-6 text-slate-600">{text}</p>
                            </article>
                        ))}
                    </div>
                </section>

                {page.details?.length > 0 && (
                    <section className="px-5 py-12 sm:px-8 lg:px-10">
                        <div className="mx-auto max-w-[1600px]">
                            {page.details.map(detail => (
                                <article key={detail.title} className="grid gap-6 border-t border-slate-200 py-8 md:grid-cols-[0.85fr_1.15fr] md:gap-14">
                                    <div>
                                        <p className="text-xs font-bold uppercase tracking-[0.1em] text-orange-700">{detail.eyebrow}</p>
                                        <h2 className="mt-3 text-2xl font-semibold leading-tight tracking-[-0.04em] text-slate-950 sm:text-3xl">{detail.title}</h2>
                                        <p className="mt-4 text-sm leading-7 text-slate-600">{detail.text}</p>
                                    </div>
                                    <ul className="divide-y divide-slate-200 border-y border-slate-200">
                                        {detail.items.map(item => (
                                            <li key={item} className="flex gap-3 py-3 text-sm font-medium leading-6 text-slate-700">
                                                <span aria-hidden="true" className="text-orange-700">—</span>{item}
                                            </li>
                                        ))}
                                    </ul>
                                </article>
                            ))}
                        </div>
                    </section>
                )}

                {page.practical?.length > 0 && (
                    <section className="px-5 py-12 sm:px-8 lg:px-10" aria-label={`${page.eyebrow} in der Praxis`}>
                        <div className="mx-auto max-w-[1600px]">
                            <p className="text-xs font-bold uppercase tracking-[0.1em] text-orange-700">In der Praxis</p>
                            <div className="mt-5 grid gap-0 border-y border-slate-200 md:grid-cols-3">
                                {page.practical.map(([title, text]) => (
                                    <article key={title} className="border-b border-slate-200 py-6 md:border-b-0 md:border-r md:px-6 md:py-7 first:md:pl-0 last:md:border-r-0 last:md:pr-0">
                                        <h2 className="text-xl font-semibold leading-snug tracking-tight text-slate-950">{title}</h2>
                                        <p className="mt-3 text-sm leading-6 text-slate-600">{text}</p>
                                    </article>
                                ))}
                            </div>
                            {page.references?.length > 0 && (
                                <p className="mt-5 text-sm text-slate-600">
                                    Grundlage bei JTL: {page.references.map(([label, href], index) => (
                                        <React.Fragment key={href}>
                                            {index > 0 && ', '}
                                            <a href={href} className="font-semibold text-orange-700 underline underline-offset-2">{label}</a>
                                        </React.Fragment>
                                    ))}
                                </p>
                            )}
                        </div>
                    </section>
                )}

                <section className="border-t border-slate-200 px-5 py-14 sm:px-8 lg:px-10">
                    <div className="mx-auto max-w-[1600px]">
                        <p className="text-xs font-bold uppercase tracking-[0.1em] text-orange-700">FAQ</p>
                        <h2 className="mt-3 text-3xl font-semibold tracking-[-0.04em] text-slate-950 sm:text-4xl">Häufige Fragen zu {page.eyebrow}</h2>
                        <div className="mt-8 grid gap-x-12 md:grid-cols-2">
                            {page.faq.map(([question, answer]) => (
                                <article key={question} className="border-t border-slate-200 py-5">
                                    <h3 className="text-base font-semibold text-slate-950">{question}</h3>
                                    <p className="mt-2 text-sm leading-6 text-slate-600">{answer}</p>
                                </article>
                            ))}
                        </div>
                    </div>
                </section>

                <section className="border-t border-slate-200 px-5 py-12 sm:px-8 lg:px-10">
                    <div className="mx-auto max-w-[1600px]">
                        <p className="text-xs font-bold uppercase tracking-[0.1em] text-orange-700">Weitere Themen</p>
                        <div className="mt-4 grid gap-x-10 sm:grid-cols-2">
                            {relatedLinks.map(([href, label]) => (
                                <a key={href} href={href} className="flex items-center justify-between border-b border-slate-200 py-4 text-sm font-semibold text-slate-700 hover:text-orange-700">
                                    {label}<ArrowRight className="h-4 w-4 text-orange-600" />
                                </a>
                            ))}
                        </div>
                    </div>
                </section>
            </main>
            <TopicFooter onLogin={onLogin} />
        </div>
    );
}
